import React, { useEffect, useState } from 'react';
import './CartPage.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import NavInf from '../../components/NavInf/NavInf';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faX } from '@fortawesome/free-solid-svg-icons';
import Alert from '../../components/Alert/Alert';
import Loader from '../../components/Loader/Loader';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { secureFetch } from '../../utils/secureFetch';
import Customers from '../../components/Customers/Customers';

const CartPage = () => {
  const [showCustomerStep, setShowCustomerStep] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [alertType, setAlertType] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [orderId, setOrderId] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(null); // null = aún no sabemos, false = no logueado
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await secureFetch('https://api.minymol.com/cart', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const items = await res.json();

        // ✅ Los precios ya vienen incluidos en product.prices (1 sola request)
        const enriched = items.map((item) => ({
          ...item,
          prices: item.product.prices || []
        }));

        setCartItems(enriched);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar el carrito:', err);
        setCartItems([]);
      } finally {
        setLoading(false);
      }
    };


    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
        fetchCart();
      } else {
        setIsLoggedIn(false); // <- Usuario no autenticado
        setLoading(false);    // <- Deja de mostrar loader
      }
    });

    return () => unsubscribe();
  }, []);

  const onUpdateQuantity = async (id, quantity) => {
    const token = localStorage.getItem('token');
    await secureFetch(`https://api.minymol.com/cart/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ quantity }),
    });

    setCartItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, quantity } : item))
    );
  };

  const onToggleCheck = async (id, isChecked) => {
    const token = localStorage.getItem('token');
    await secureFetch(`https://api.minymol.com/cart/${id}/check`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ isChecked }),
    });
    setCartItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isChecked } : item))
    );
  };

  const onDeleteItem = async (id) => {
    const token = localStorage.getItem('token');
    await secureFetch(`https://api.minymol.com/cart/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const grouped = cartItems.reduce((acc, item) => {
    const prov = item.providerNameSnapshot || 'Proveedor desconocido';
    if (!acc[prov]) acc[prov] = [];
    acc[prov].push(item);
    return acc;
  }, {});

  const getApplicablePrice = (item) => {
    return item.prices.find((p) => {
      const qList = p.quantity?.split(',').map((q) => parseInt(q.trim()));
      return qList?.includes(item.quantity);
    });
  };

  const calculateSubtotal = (item) => {
    const unitPrice = parseFloat(getApplicablePrice(item)?.price || '0');
    return unitPrice * item.quantity;
  };

  const calculateProviderTotal = (items) =>
    items
      .filter((item) => item.isChecked)
      .reduce((sum, item) => sum + calculateSubtotal(item), 0);

  const handleRequestByProvider = async (providerId, items, customer) => {
    const usuario = JSON.parse(localStorage.getItem('usuario'));
    const token = localStorage.getItem('token');
    const userId = usuario.id;

    const checkedItems = items.filter((item) => item.isChecked);
    if (checkedItems.length === 0) {
      setAlertType('error');
      setAlertMessage('Selecciona al menos un producto para este proveedor.');
      setShowAlert(true);
      return;
    }

    setIsCreatingOrder(true); // Activar loading

    const orderItems = checkedItems.map((item) => ({
      productId: item.product.id,
      productName: item.productNameSnapshot,
      quantity: item.quantity,
      unity: 'unidad',
      unitPrice: parseFloat(getApplicablePrice(item)?.price || '0'),
      color: item.colorSnapshot,
      size: item.sizeSnapshot,
      imageSnapshot: item.imageUrlSnapshot,
    }));

    const totalPrice = orderItems.reduce((sum, item) => sum + (item.unitPrice) * item.quantity, 0);

    try {
      const res = await secureFetch('https://api.minymol.com/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          providerId,
          customerId: customer.id,
          totalPrice,
          items: orderItems,
          notes: 'Hola, he colocado un pedido desde la tienda.',
        }),
      });

      if (!res.ok) throw new Error('Error al crear el pedido');
      const order = await res.json();

      for (const item of checkedItems) {
        await secureFetch(`https://api.minymol.com/cart/${item.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setCartItems((prev) =>
        prev.filter((item) => item.providerId !== providerId || !item.isChecked)
      );

      const shareRes = await fetch('https://api.minymol.com/share', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: `Pedido #${order.id}`,
          imageUrl: checkedItems[0].imageUrlSnapshot,
          redirectUrl: `https://minymol.com/orden/${order.id}`,
        }),
      });

      if (!shareRes.ok) throw new Error('No se pudo generar enlace compartido');
      const { shareUrl } = await shareRes.json();

      const providerRes = await fetch(`https://api.minymol.com/providers/public/${providerId}`);
      if (!providerRes.ok) throw new Error('No se pudo obtener datos del proveedor');
      const providerData = await providerRes.json();

      await fetch('https://api.minymol.com/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel: 'whatsapp',
          purpose: 'new_order',
          phoneNumber: providerData.telefono,
          data: {
            proveedor: providerData.nombre_empresa,
            cliente: customer.nombre ?? 'Cliente',
            ciudad: customer.ciudad ?? 'Sin ciudad',
            departamento: customer.departamento ?? 'Sin departamento',
            orden: order.id,
            valor: totalPrice.toLocaleString('es-CO'),
            url: shareUrl,
          },
        }),
      });

      await fetch('https://api.minymol.com/push/send-to-provider', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          providerId,
          title: `Nuevo pedido recibido`,
          body: `Has recibido un nuevo pedido del cliente ${usuario.nombre} (#${order.id})`,
          data: {
            url: `https://minymol.com/orden/${order.id}`,
            orderId: order.id,
          },
          type: 'order'
        })
      });

      setOrderId(order.id);

      setAlertType('success');
      setAlertMessage('Orden agregada con éxito.');

      setTimeout(() => {
        setShowAlert(true);
      }, 50);

    } catch (err) {
      console.error('Error al solicitar el pedido:', err);
      setAlertType('error');
      setAlertMessage('Hubo un error al crear el pedido.');
      setShowAlert(true);
    } finally {
      setIsCreatingOrder(false); // Desactivar loading
    }
  };

  if (loading) return <Loader />;

  if (isLoggedIn === false) {
    return (
      <>
        <Header />
        <div className="cart-page-empty">
          <h2>Debes iniciar sesión para ver tu carrito</h2>
        </div>
        <NavInf />
        <Footer />
      </>
    );
  }


  if (!loading && cartItems.length === 0) {
    return (
      <>
        <Header />
        <div className="cart-page-empty">
          <h2>No hay productos en el carrito</h2>
        </div>
        <NavInf />
        <Footer />
      </>
    );
  }


  return (
    <>
      {showAlert && (
        <Alert
          type={alertType}
          message={alertMessage}
          onClose={() => setShowAlert(null)}
          redirectTo={alertType === 'success' && orderId ? `/orden/${orderId}` : null}
        />
      )}
      <Header />
      <div className="computadora-container">
        <div className="cart-page">

          <div className="request-all">
            {(() => {
              const proveedoresConPedidos = Object.values(grouped).filter(items =>
                items.some(item => item.isChecked)
              ).length;

              if (proveedoresConPedidos === 0) return null;

              return (
                <p className="providers-count-msg">
                  Tienes pedidos para <span>{proveedoresConPedidos === 1
                    ? 'un solo proveedor'
                    : `${proveedoresConPedidos} proveedores`}</span>
                </p>
              );
            })()}
          </div>

          {Object.entries(grouped).map(([provider, items]) => (
            <fieldset key={provider} className="provider-group">
              <legend>{provider}</legend>
              {items.map((item) => {
                const applicablePrice = getApplicablePrice(item);
                const unitPrice = parseFloat(applicablePrice?.price || 0);
                return (
                  <div key={item.id} className={`cart-item ${item.isChecked ? 'checked' : ''}`}>
                    <div className="item-info">
                      <div className="item-image">
                        <input
                          type="checkbox"
                          className="check-product"
                          checked={item.isChecked}
                          onChange={(e) => onToggleCheck(item.id, e.target.checked)}
                        />
                        <img src={item.imageUrlSnapshot} alt={item.productNameSnapshot} />
                      </div>
                      <div className="item-details">
                        <span className="product-name">{item.productNameSnapshot}</span>
                        <p className="product-variant">
                          Talla: {item.sizeSnapshot || 'N/A'} | Color: {item.colorSnapshot || 'N/A'}
                        </p>
                        <div className="tooltip-wrapper">
                          {item.prices.length > 1 ? (
                            <>
                              <span className="price-more-info">+{item.prices.length - 1} precios</span>
                              <div className="tooltip-content">
                                {item.prices.slice(1).map((p, i) => (
                                  <p key={i}><strong>${parseFloat(p.price).toLocaleString('es-CO')}</strong> {p.description}</p>
                                ))}
                              </div>
                            </>
                          ) : (
                            <span className="price-more-info" style={{ visibility: 'hidden' }}>.</span>
                          )}
                        </div>
                        <span className="product-price">
                          <p>COP</p>{unitPrice.toLocaleString('es-CO', { minimumFractionDigits: 0 })}
                        </span>
                      </div>
                    </div>
                    <p className="product-total">
                      Total: ${(unitPrice * item.quantity).toLocaleString('es-CO')}
                    </p>
                    <div className="item-controls">
                      <select
                        className="quantity-selector"
                        value={item.quantity}
                        onChange={(e) => onUpdateQuantity(item.id, parseInt(e.target.value))}
                      >
                        {[...new Set(item.prices.flatMap(p => p.quantity?.split(',') || []))]
                          .map(q => q.trim()).filter(q => q).map((q, i) => (
                            <option key={i} value={parseInt(q)}>{q}</option>
                          ))}
                      </select>
                      <FontAwesomeIcon id="item-delete" icon={faTrash} onClick={() => onDeleteItem(item.id)} />
                    </div>
                  </div>
                );
              })}
              <div className="provider-total">
                Subtotal: COP {calculateProviderTotal(items).toLocaleString('es-CO')}
              </div>
              <button
                className="request-btn"
                onClick={() => !isCreatingOrder && setShowCustomerStep(items[0].product.providerId)}
                disabled={isCreatingOrder}
                style={{ opacity: isCreatingOrder ? 0.6 : 1 }}
              >
                {isCreatingOrder ? 'Solicitando pedido...' : 'Solicitar pedido para este proveedor'}
              </button>

            </fieldset>
          ))}
        </div>
      </div>
      <NavInf selected={"cart"} />
      <Footer navinf={true} />
      {showCustomerStep && (
        <div className="customer-modal-overlay">
          <div className="customer-modal-content">
            <button className='close-modal-btn-user' onClick={() => setShowCustomerStep(false)}>
              <FontAwesomeIcon icon={faX} />
            </button>
            <Customers
              providerId={showCustomerStep}
              onContinue={(customer) => {
                if (isCreatingOrder) return; // Prevenir múltiples envíos
                setShowCustomerStep(null);
                handleRequestByProvider(
                  showCustomerStep,
                  cartItems.filter((item) => item.product.providerId === showCustomerStep),
                  customer
                );
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CartPage;
